import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, Send } from "lucide-react";

function timeAgo(iso) {
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "ahora";
  if (mins < 60) return `hace ${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `hace ${h}h`;
  return d.toLocaleDateString("es-AR");
}

export default function CommentsSection({ articleId }) {
  const [comments, setComments] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", body: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/comments", { params: { article_id: articleId } });
      setComments(r.data || []);
    } catch (err) { console.warn("[popu]", err); }
  };

  useEffect(() => { load(); }, [articleId]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        article_id: articleId,
        name: form.name,
        body: form.body,
        ...(form.email ? { email: form.email } : {}),
      };
      await api.post("/comments", payload);
      toast.success("Comentario enviado. Se publicará tras moderación.");
      setForm({ name: "", email: "", body: "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? (d[0]?.msg || "Error enviando") : "Error enviando";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mt-10 pt-8 border-t" style={{ borderColor: "var(--border)" }} data-testid="comments-section">
      <h2 className="font-headline uppercase text-2xl md:text-3xl mb-6 flex items-center gap-2">
        <MessageCircle size={22} /> Comentarios <span className="text-sm font-normal" style={{ color: "var(--text-muted)" }}>({comments.length})</span>
      </h2>

      <form onSubmit={submit} className="mb-8 space-y-3 p-5 rounded-sm border" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="comment-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            required
            placeholder="Tu nombre *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="px-3 py-2 border rounded-sm bg-transparent outline-none focus:border-[color:var(--accent)]"
            style={{ borderColor: "var(--border)" }}
            data-testid="comment-name"
          />
          <input
            type="email"
            placeholder="Email (no se publica)"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="px-3 py-2 border rounded-sm bg-transparent outline-none focus:border-[color:var(--accent)]"
            style={{ borderColor: "var(--border)" }}
          />
        </div>
        <textarea
          required
          placeholder="Tu comentario *"
          minLength={2}
          rows={3}
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          className="w-full px-3 py-2 border rounded-sm bg-transparent outline-none focus:border-[color:var(--accent)] resize-none"
          style={{ borderColor: "var(--border)" }}
          data-testid="comment-body"
        />
        <div className="flex items-center justify-between">
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>Los comentarios pasan por moderación.</p>
          <button type="submit" disabled={loading} className="btn-accent disabled:opacity-50" data-testid="comment-submit">
            {loading ? "Enviando..." : "Comentar"} <Send size={14} />
          </button>
        </div>
      </form>

      {comments.length === 0 ? (
        <p className="text-center py-6 text-sm" style={{ color: "var(--text-muted)" }}>
          Sé el primero en comentar 💬
        </p>
      ) : (
        <ul className="space-y-5">
          {comments.map((c) => (
            <li key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
              <div
                className="w-10 h-10 rounded-full accent-bg text-white flex items-center justify-center font-bold shrink-0"
              >
                {c.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold flex items-center gap-2">
                  {c.name}
                  <span className="text-xs font-normal" style={{ color: "var(--text-muted)" }}>{timeAgo(c.created_at)}</span>
                </p>
                <p className="mt-1 leading-relaxed text-sm whitespace-pre-wrap" style={{ color: "var(--text)" }}>{c.body}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
